import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Alert, Card, FormStack } from "@marekl/adminer/components";
import { BrandLockup } from "../../../components/BrandLockup";
import { SupportContacts } from "../../../components/SupportContacts";
import { useLogin } from "../../../auth/queries";
import { ApiError } from "../../../lib/api";
import { getSafeReturnTo } from "../../../auth/returnTo";
import { PublicNotices, forgetDismissedNotices } from "../../../components/Notices";
import styles from "./LoginPage.module.css";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeReturnTo(searchParams.get("returnTo"));

  const { mutate: login, isPending, error } = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    login(
      { identifier: email, password },
      {
        onSuccess: () => {
          forgetDismissedNotices();
          navigate(returnTo, { replace: true });
        },
      },
    );
  }

  function errorMessage(): string | undefined {
    if (!error) return undefined;
    if (error instanceof ApiError) {
      const body = error.body as { message?: string } | null;
      return body?.message ?? "Přihlášení selhalo.";
    }
    return "Neočekávaná chyba. Zkuste to prosím znovu.";
  }

  return (
    <div className={styles.page}>
      <div className={styles.notices}>
        <PublicNotices />
      </div>
      <Card className={styles.card}>
        <Card.Content>
          <div className={styles.header}>
            <BrandLockup />
            <p className={styles.subtitle}>Přihlášení</p>
          </div>

          <FormStack as="form" onSubmit={handleSubmit} noValidate className={styles.form}>
            {errorMessage() && (
              <Alert variant="err">{errorMessage()}</Alert>
            )}

            <Input
              id="email"
              label="E-mail"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              id="password"
              label="Heslo"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Button
              type="submit"
              variant="primary"
              block
              loading={isPending}
              disabled={!email || !password}
            >
              Přihlásit se
            </Button>

            <p className={styles.registerLink}>
              <Link to="/obnoveni-hesla">Zapomenuté heslo?</Link>
            </p>

            <p className={styles.registerLink}>
              Nemáte ještě účet? <Link to="/registrace-tymu">Registrovat nový tým</Link>
            </p>
          </FormStack>
        </Card.Content>
      </Card>
      <SupportContacts />
    </div>
  );
}
